import React from 'react'
import { Alert } from 'antd'
import { useRefinementFlow } from './hooks/useRefinementFlow'
import { useSelector } from 'react-redux'
import { rubricRefinementSelectors } from '../../../../../../store/selectors/rubricRefinement.selectors'

// Simple constants for UI-only message types
export const WELCOME_MESSAGE = 'welcome'
export const SYSTEM_MESSAGE = 'system'
export const THINKING_MESSAGE = 'thinking'

// Simple thinking states
export const THINKING_STATES = {
    INITIALIZING: 'initializing',
    PROCESSING: 'processing',
}

/**
 * Custom hook that manages messages to display in the chat UI
 * Instead of transforming RefinementItems into different message objects,
 * we use the RefinementItems directly and only add UI-specific messages
 */
export const useMessages = () => {
    const { refinementItems, refinementProcess, hasStarted, isAllItemsReviewed, isWaitingForNextItem, lastItem } =
        useRefinementFlow()
    
    const hasApprovedSuggestions = useSelector(rubricRefinementSelectors.hasApprovedSuggestions)

    const messages = []

    // First message is always the welcome message
    messages.push({
        id: 'welcome',
        type: WELCOME_MESSAGE,
    })

    // If the user hasn't clicked start yet, only show the welcome message
    if (!hasStarted) return { messages, isAllItemsReviewed: false }

    // If the process is initializing, show the initialization message
    if (refinementProcess?.status === 'Initializing') {
        messages.push({
            id: 'initializing',
            type: THINKING_MESSAGE,
            state: THINKING_STATES.INITIALIZING,
        })
        return { messages, isAllItemsReviewed: false }
    }

    // If the process has started but there are no items to review
    if (refinementProcess && !refinementProcess.item && refinementItems.length === 0) {
        messages.push({
            id: 'no-items',
            type: SYSTEM_MESSAGE,
            content: 'There are currently no submissions that need category refinement.',
        })
        return { messages, isAllItemsReviewed: true }
    }

    if (refinementItems.length === 0) {
        messages.push({
            id: 'processing',
            type: THINKING_MESSAGE,
            state: THINKING_STATES.PROCESSING,
        })
        return { messages, isAllItemsReviewed: false }
    }

    // Simply add all refinement items to messages
    // The UI components will render different views based on the item type
    refinementItems.forEach(item => {
        messages.push(item)
    })

    // Add thinking message if we're waiting for the next item
    if (isWaitingForNextItem) {
        messages.push({
            id: 'waiting-' + (lastItem?.id || 'unknown'),
            type: THINKING_MESSAGE,
            state: THINKING_STATES.PROCESSING,
        })
    }

    // Only show status message if all items are reviewed and we're not waiting for a response
    if (isAllItemsReviewed) {
        if (hasApprovedSuggestions) {
            messages.push({
                id: 'complete',
                type: SYSTEM_MESSAGE,
                content: (
                    <Alert
                        className='completion-message'
                        type='success'
                        message='🎉 Excellent work! You have completed all rubric refinements. Your careful consideration has helped eliminate ambiguity in the grading criteria. Ready to update your model with these enhancements?'
                    />
                ),
            })
        } else {
            messages.push({
                id: 'complete-no-updates',
                type: SYSTEM_MESSAGE,
                content: (
                    <Alert
                        className='completion-message'
                        type='success'
                        message='No Updates Needed. The current rubric for this question is consistent with your selections.'
                    />
                ),
            })
        }
    }

    return { messages, isAllItemsReviewed, hasApprovedSuggestions }
}
